import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { Container, Row, Col, Form, Modal, Button, Accordion, Alert, Spinner } from "react-bootstrap";
import NavbarCustom from "../Components/Navbar";
import Footer from "../Components/Footer";
import { Map, Bus, BookOpen, Utensils, Send, CheckCircle, GraduationCap,
   Plus, Trash2, FileText, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import emailjs from '@emailjs/browser';
import customTourBuilderImg from "./images/Custom-Tour-Builder.webp"
import specialHeroImg from './images/special-hero.webp';
import ReCAPTCHA from "react-google-recaptcha";
import "./SpecialPage.css";

import SEO from "../Components/SEO";

function SpecialPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const lang = (i18n.language || 'en').split('-')[0];
  const navigate = useNavigate();

  const seoTexts = {
    custom: {
      title: {
        en: "Custom Tour Builder",
        ru: "Индивидуальный тур",
        hy: "Անհատական տուր"
      },
      description: {
        en: "Design your own private tour of Armenia — choose destinations, dates and group size, and we'll handle the logistics.",
        ru: "Создайте собственный частный тур по Армении — выберите направления, даты и количество человек, а логистику возьмём на себя мы.",
        hy: "Ստեղծեք ձեր սեփական մասնավոր տուրը Հայաստանում. ընտրեք ուղղությունները, ամսաթվերը և խմբի չափը, իսկ լոգիստիկան կհոգանք մենք."
      }
    },
    school: {
      title: {
        en: "School Tours",
        ru: "Школьные туры",
        hy: "Դպրոցական տուրեր"
      },
      description: {
        en: "Educational school trips across Armenia with professional guides, transport and tailored programs.",
        ru: "Образовательные школьные поездки по Армении с профессиональными гидами, транспортом и индивидуальными программами.",
        hy: "Կրթական դպրոցական ճամփորդություններ Հայաստանում՝ պրոֆեսիոնալ գիդերով, տրանսպորտով և հարմարեցված ծրագրերով."
      }
    }
  };

  const recaptchaRef = useRef();
  const captchaTokenRef = useRef(null);

  // --- ЛОГИКА ВКЛАДОК И URL ---
  // Читаем ?tab= из URL, если его нет — ставим "custom"
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get("tab") || "custom";
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [alertInfo, setAlertInfo] = useState({ show: false, variant: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const alertRef = React.useRef(null);

  // Синхронизация: если URL изменился (клик в Navbar), меняем вкладку
  // Синхронизация: если URL изменился (клик в Navbar), меняем вкладку
  useEffect(() => {

    window.scrollTo(0, 0);

    const tabFromUrl = new URLSearchParams(location.search).get("tab");
    // Проверяем, что в URL есть вкладка, и она отличается от текущей
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [location.search, activeTab]); // Теперь activeTab в зависимостях, и ESLint доволен

  // Функция смены вкладки с обновлением URL (useCallback важен для стабильности)
  const handleTabChange = React.useCallback((tabName) => {
    setActiveTab(tabName);
    navigate(`?tab=${tabName}`, { replace: true });
  }, [navigate]);

  // --- СОСТОЯНИЯ ФОРМЫ ---
  const [destinations, setDestinations] = useState([""]);
  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    peopleCount: 1,
    date: "",
    wishes: ""
  });

  const [showModal, setShowModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    const tabFromUrl = new URLSearchParams(location.search).get("tab");
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [location.search, activeTab]);

  const addDestination = () => setDestinations([...destinations, ""]);

  const handleDestChange = (index, value) => {
    const updatedDestinations = [...destinations];
    updatedDestinations[index] = value;
    setDestinations(updatedDestinations);
  };

  const removeDestination = (index) => {
    if (destinations.length > 1) {
      setDestinations(destinations.filter((_, i) => i !== index));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleShowPreview = async (e) => {
    e.preventDefault();

  recaptchaRef.current.reset();

  let token;
  try {
    token = await recaptchaRef.current.executeAsync();
  } catch (err) {
    // покажите ошибку пользователю если нужно
    return;
  }

  if (!token) return;

  captchaTokenRef.current = token;
  setShowModal(true);
  };

  const handleFinalSubmit = () => {
    setIsSubmitting(true);

    const formattedDestinations = destinations
      .filter(d => d.trim() !== "")
      .map((d, i) => `${i + 1}. ${d}`)
      .join("\n");

    const templateParams = {
      tour_type: activeTab === "custom" ? "Custom Tour Builder" : "School Tour",
      email: formData.email,
      phone: formData.phone,
      people: formData.peopleCount,
      date: formData.date,
      destinations: activeTab === "custom" ? formattedDestinations : "Fixed School Program",
      wishes: formData.wishes || "No special wishes",
      'g-recaptcha-response': captchaTokenRef.current
    };

    emailjs.send(
      'service_zvppy78',
      'template_mpwegyd',
      templateParams, 
      'P2-IFz5S0EKcKVf9p'
    )
    .then(() => {
        setShowModal(false); 
        setAlertInfo({ 
          show: true, 
          variant: "success", 
          message: t("special.form.success_msg") || "Success! Your request has been sent." 
        });

        setTimeout(() => {
            alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 200);

        setDestinations([""]);
        setFormData({ email: "", phone: "", peopleCount: 1, date: "", wishes: "" });
        setTimeout(() => setAlertInfo({ show: false, variant: "", message: "" }), 8000);
    })
    .catch(() => {
        setShowModal(false);
        setAlertInfo({ 
          show: true, 
          variant: "danger", 
          message: t("special.form.error_msg") || "Error sending message." 
        });
    })
    .finally(() => {
        setIsSubmitting(false);
    });
  };

  return (
    <div className="special-page">
      <SEO
        title={seoTexts[activeTab]?.title[lang] || seoTexts.custom.title.en}
        description={seoTexts[activeTab]?.description[lang] || seoTexts.custom.description.en}
        url={`/special${location.search}`}
        lang={lang}
      />
      <NavbarCustom />

      {/* HERO SECTION */}
      <section className="special-hero" style={{ backgroundImage: `linear-gradient(rgba(11, 22, 13, 0.7), rgba(11, 22, 13, 0.7)), url(${specialHeroImg})`}}>
        <div className="special-hero-content">
          <h1>{t("special.hero.title")}</h1>
          <p>{t("special.hero.subtitle")}</p>
        </div>
      </section>

      {/* TAB CONTROL */}
      <section className="tab-control-section">
        <Container>
          <div className="tab-switcher-island">
            <button 
              className={`tab-btn ${activeTab === "custom" ? "active" : ""}`}
              onClick={() => handleTabChange("custom")} // Используй handleTabChange!
            >
              <Map size={20} className="tab-icon" />
              {t("special.tabs.custom")}
            </button>
            <button 
              className={`tab-btn ${activeTab === "school" ? "active" : ""}`}
              onClick={() => handleTabChange("school")} // Используй handleTabChange!
            >
              <Bus size={20} className="tab-icon" />
              {t("special.tabs.school")}
            </button>
          </div>
        </Container>
      </section>

      <section className="special-content-area">
        <Container>
          
          {/* === CUSTOM TOUR BUILDER === */}
          {activeTab === "custom" && (
            <div className="content-fade-in">
              <div className="custom-route-grid">
                
                <div className="custom-text-side">
                  <span className="section-badge">{t("special.custom.badge")}</span>
                  <h2>{t("special.custom.title")}</h2>
                  <p className="description">{t("special.custom.desc")}</p>
                  
                  <ul className="benefits-list">
                    <li><CheckCircle size={18} /> {t("special.custom.benefit1")}</li>
                    <li><CheckCircle size={18} /> {t("special.custom.benefit2")}</li>
                    <li><CheckCircle size={18} /> {t("special.custom.benefit3")}</li>
                  </ul>

                  {/* === НОВЫЙ БЛОК С ИЗОБРАЖЕНИЕМ === */}
                <div className="sp-custom-image-block mt-5">
                    <img src={customTourBuilderImg} alt="Planning custom route" />
                </div>
                </div>

                <div className="custom-form-side">
                  <div className="form-island">

                    {/* --- КОНТЕЙНЕР ДЛЯ АЛЕРТА С РЕФОМ --- */}
                    <div ref={alertRef}>
                      {alertInfo.show && (
                        <Alert 
                          variant={alertInfo.variant} 
                          onClose={() => setAlertInfo({ show: false, variant: "", message: "" })} 
                          dismissible
                          className={`custom-alert-${alertInfo.variant} mb-4`}
                          style={{ 
                            borderRadius: '12px',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.05)' 
                          }}
                        >
                          <div className="d-flex align-items-center">
                            {alertInfo.variant === "success" && <CheckCircle size={24} className="me-3"/>}
                            <div>
                              <strong>{alertInfo.variant === "success" ? "Done!" : "Oops!"}</strong>
                              <div className="small">{alertInfo.message}</div>
                            </div>
                          </div>
                        </Alert>
                      )}
                    </div>

                    <h3>{t("special.form.header")}</h3>
                    <Form onSubmit={handleShowPreview}>
                      
                      {/* ДИНАМИЧЕСКИЕ МЕСТА */}
                      <Form.Group className="mb-4">
                        <Form.Label className="fw-bold">{t("special.form.places_label")}</Form.Label>
                        {destinations.map((dest, index) => (
                          <div key={index} className="destination-input-wrapper mb-2">
                            <Form.Control 
                              type="text" 
                              required
                              placeholder={`${t("special.form.place_placeholder")} ${index + 1}`}
                              value={dest}
                              onChange={(e) => handleDestChange(index, e.target.value)}
                            />
                            {destinations.length > 1 && (
                              <button type="button" className="remove-dest-btn" onClick={() => removeDestination(index)}>
                                <Trash2 size={18} />
                              </button>
                            )}
                          </div>
                        ))}
                        <button type="button" className="add-dest-btn" onClick={addDestination}>
                          <Plus size={16} /> {t("special.form.add_place")}
                        </button>
                      </Form.Group>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>{t("special.form.email_label")}</Form.Label>
                            <Form.Control type="email" name="email" required onChange={handleInputChange} />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>{t("special.form.phone_label")}</Form.Label>
                            <Form.Control type="tel" name="phone" required onChange={handleInputChange} />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col xs={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>{t("special.form.people_label")}</Form.Label>
                            <Form.Control type="number" name="peopleCount" min="1" defaultValue="1" onChange={handleInputChange} />
                          </Form.Group>
                        </Col>
                        <Col xs={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>{t("special.form.date_label")}</Form.Label>
                            <Form.Control type="date" name="date" required onChange={handleInputChange} />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="mb-4">
                        <Form.Label>{t("special.form.wishes_label")}</Form.Label>
                        <Form.Control as="textarea" rows={4} name="wishes" placeholder={t("special.form.wishes_placeholder")} onChange={handleInputChange} />
                      </Form.Group>

                      <button type="submit" className="submit-btn-full">
                        {t("special.form.submit_btn")} <Send size={18} />
                      </button>

                      <p className="recaptcha-disclaimer mt-3 mb-0">
                        {t("terms.form.recaptcha_text")}{" "}
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                          {t("terms.form.recaptcha_privacy")}
                        </a>{" "}
                        {t("terms.form.recaptcha_and")}{" "}
                        <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">
                          {t("terms.form.recaptcha_terms")}
                        </a>{" "}
                        {t("terms.form.recaptcha_apply")}
                      </p>
                    </Form>
                  </div>
                </div>

              </div>

              {/* === БЛОК ПРЯМОЙ СВЯЗИ === */}
              <div className="sp-direct-contact-box">
                <div className="sp-direct-content">
                  <div className="sp-direct-text">
                    <h4>{t("special.direct.title", "Have more questions?")}</h4>
                    <p>{t("special.direct.desc", "Skip the form and reach us directly via your favorite platform.")}</p>
                  </div>
                  <div className="sp-direct-actions">
                    <a href="https://wa.me/37495283022" target="_blank" rel="noreferrer" className="sp-action-link whatsapp">
                      <i className="fab fa-whatsapp" style={{ fontSize: '1.1rem' }} />
                      <span>WhatsApp</span>
                    </a>
                    <a href="https://t.me/parmanitour" target="_blank" rel="noreferrer" className="sp-action-link telegram">
                      <i className="fab fa-telegram" style={{ fontSize: '1.1rem' }} />
                      <span>Telegram</span>
                    </a>
                    <a href="viber://chat?number=%2B37495283022" className="sp-action-link viber">
                      <i className="fab fa-viber" style={{ fontSize: '1.1rem' }} />
                      <span>Viber</span>
                    </a>
                    <a href="mailto:parmanitour@gmail.com" className="sp-action-link email">
                      <i className="fas fa-envelope" style={{ fontSize: '1.1rem' }} />
                      <span>Email</span>
                    </a>
                  </div>
                </div>
              </div>
              {/* {CustomFAQ()} */}
              <CustomFAQ t={t} />
            </div>
          )}

          {/* === SCHOOL TOURS === */}
          {activeTab === "school" && (
            <div className="content-fade-in">
              <div className="school-header text-center mb-5">
                <h2>{t("special.school.title")}</h2>
                <p>{t("special.school.subtitle")}</p>
              </div>

              <Row className="g-4 mb-5">
                <Col md={4}>
                  <div className="school-feature-card">
                    <div className="icon-box yellow"><Bus size={32} /></div>
                    <h4>{t("special.school.feat1_title")}</h4>
                    <p>{t("special.school.feat1_text")}</p>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="school-feature-card">
                    <div className="icon-box green"><BookOpen size={32} /></div>
                    <h4>{t("special.school.feat2_title")}</h4>
                    <p>{t("special.school.feat2_text")}</p>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="school-feature-card">
                    <div className="icon-box red"><Utensils size={32} /></div>
                    <h4>{t("special.school.feat3_title")}</h4>
                    <p>{t("special.school.feat3_text")}</p>
                  </div>
                </Col>
              </Row>

              <SchoolToursAlbum />

              <div className="school-cta-banner">
                <div className="school-cta-content">
                  <h3>{t("special.school.cta_title")}</h3>
                  <p>{t("special.school.cta_text")}</p>
                  <div className="cta-buttons">
                    <button className="btn-primary-school" onClick={() => setShowContactModal(true)}>
                      {t("special.school.cta_btn")}
                    </button>
                    <Modal show={showContactModal} onHide={() => setShowContactModal(false)} centered>
                      <Modal.Header closeButton>
                        <Modal.Title>{t("special.school.contact_modal_title", "Contact Us")}</Modal.Title>
                      </Modal.Header>
                      <Modal.Body className="p-4">
                        <p className="text-muted text-center mb-4">
                          {t("special.school.contact_modal_desc", "Choose your preferred way to reach us")}
                        </p>
                        <div className="d-flex flex-column gap-3">
                          <a href="https://wa.me/37495283022" target="_blank" rel="noreferrer" className="sp-action-link whatsapp w-100 justify-content-center">
                            <i className="fab fa-whatsapp" style={{ fontSize: '1.2rem' }} />
                            <span>WhatsApp</span>
                          </a>
                          <a href="https://t.me/parmanitour" target="_blank" rel="noreferrer" className="sp-action-link telegram w-100 justify-content-center">
                            <i className="fab fa-telegram" style={{ fontSize: '1.2rem' }} />
                            <span>Telegram</span>
                          </a>
                          <a href="viber://chat?number=%2B37495283022" className="sp-action-link viber w-100 justify-content-center">
                            <i className="fab fa-viber" style={{ fontSize: '1.2rem' }} />
                            <span>Viber</span>
                          </a>
                          <a href="mailto:parmanitour@gmail.com" className="sp-action-link email w-100 justify-content-center">
                            <i className="fas fa-envelope" style={{ fontSize: '1.2rem' }} />
                            <span>Email</span>
                          </a>
                        </div>
                      </Modal.Body>
                    </Modal>
                  </div>
                </div>
              </div>
              {/* {SchoolFAQ()} */}
              <SchoolFAQ t={t} />
            </div>
          )}

        </Container>
      </section>

      {/* МОДАЛЬНОЕ ОКНО - ПРОВЕРКА ДАННЫХ (RECEIPT) */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered className="receipt-modal">
        <Modal.Header closeButton>
          <Modal.Title><FileText className="me-2" /> {t("special.modal.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="receipt-content">
            <div className="receipt-section mb-3">
              <label className="text-muted small">{t("special.form.places_label")}</label>
              <ul className="receipt-dest-list">
                {destinations.map((d, i) => d && <li key={i}>{d}</li>)}
              </ul>
            </div>
            <hr />
            <div className="receipt-grid">
              <div className="receipt-item">
                <span className="text-muted small d-block">{t("special.form.date_label")}</span>
                <strong>{formData.date}</strong>
              </div>
              <div className="receipt-item">
                <span className="text-muted small d-block">{t("special.form.people_label")}</span>
                <strong>{formData.peopleCount}</strong>
              </div>
              <div className="receipt-item">
                <span className="text-muted small d-block">{t("special.form.email_label")}</span>
                <strong>{formData.email}</strong>
              </div>
              <div className="receipt-item">
                <span className="text-muted small d-block">{t("special.form.phone_label")}</span>
                <strong>{formData.phone}</strong>
              </div>
            </div>
            {formData.wishes && (
              <div className="receipt-section mt-3">
                <label className="text-muted small">{t("special.form.wishes_label")}</label>
                <p className="receipt-wishes">{formData.wishes}</p>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="link" className="text-decoration-none text-muted" onClick={() => setShowModal(false)}>
            {t("special.modal.edit")}
          </Button>
          <Button 
            className="btn-confirm-final" 
            variant="success"
            onClick={handleFinalSubmit}
            disabled={isSubmitting} // Блокируем
          >
            {isSubmitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                {t("special.modal.sending") || "Sending..."}
              </>
            ) : (
              t("special.modal.confirm")
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Одна капча для всей страницы */}
      <ReCAPTCHA
        ref={recaptchaRef}
        size="invisible"
        sitekey="6Lc3ZHMsAAAAAOWWtv3oxB3DtuzHbvNZrdrSOdU1"
      />

      <Footer />
    </div>
  );




function SchoolToursAlbum() {
  const { i18n, t } = useTranslation();
  const currentLang = i18n.language || 'en';
  const scrollRef = React.useRef(null);

  const schoolToursData = [
    {
      id: 1,
      image: "https://res.cloudinary.com/dwqsqiezw/image/upload/v1780395215/history-hero_osuvqi.webp",
      title: { 
        en: "Yerevan City Landmarks", 
        ru: "Достопримечательности Еревана", 
        hy: "Երևանի տեսարժան վայրերը" 
      },
      location: { en: "Yerevan", ru: "Ереван", hy: "Երևան" },
      age: { en: "8-15 Years", ru: "8-15 Лет", hy: "8-15 Տարեկան" }
    },
    {
      id: 2,
      image: "https://res.cloudinary.com/dwqsqiezw/image/upload/v1780395514/Armenian_alphabet_001_an535p.webp",
      title: { 
        en: "Aragatsotn: Oshakan, Alphabet Alley & Byurakan", 
        ru: "Арагацотн: Ошакан, Аллея букв и Бюракан", 
        hy: "Արագածոտն՝ Օշական, Տառերի պուրակ և Բյուրական" 
      },
      location: { en: "Aragatsotn", ru: "Арагацотн", hy: "Արագածոտն" },
      age: { en: "8-15 Years", ru: "8-15 Лет", hy: "8-15 Տարեկան" }
    },
    {
      id: 3,
      image: "https://res.cloudinary.com/dwqsqiezw/image/upload/w_600,c_fill,g_auto,f_auto,q_auto/v1776165535/etchmiadzin1_aqmnrd.webp",
      title: { 
        en: "Holy Etchmiadzin Journey", 
        ru: "Путешествие в Святой Эчмиадзин", 
        hy: "Ճանապարհորդություն Սուրբ Էջմիածին" 
      },
      location: { en: "Etchmiadzin", ru: "Эчмиадзин", hy: "Էջմիածին" },
      age: { en: "8-15 Years", ru: "8-15 Лет", hy: "8-15 Տարեկան" }
    },
    {
      id: 4,
      image: "https://res.cloudinary.com/dwqsqiezw/image/upload/v1780349955/main_ywabtb.webp",
      title: { 
        en: "Garni & Tsaghkadzor Adventure", 
        ru: "Приключения в Гарни и Цахкадзоре", 
        hy: "Գառնի և Ծաղկաձոր արկածներ" 
      },
      location: { en: "Garni & Tsaghkadzor", ru: "Гарни и Цахкадзор", hy: "Գառնի և Ծաղկաձոր" },
      age: { en: "8-15 Years", ru: "8-15 Лет", hy: "8-15 Տարեկան" }
    }
  ];

  const scroll = (direction) => {
    if (scrollRef.current) {
      const { current } = scrollRef;
      // clientWidth дает чистую ширину видимой области без скроллбаров
      const scrollAmount = current.clientWidth + 24; // ширина окна + gap
      
      current.scrollBy({ 
        left: direction === "left" ? -scrollAmount : scrollAmount, 
        behavior: "smooth" 
      });
    }
  };

  return (
    <div className="school-album-section">
      <Container>
        {/* Заголовок и подзаголовок */}
        <div className="album-intro text-center">
          <div className="intro-badge">
            <GraduationCap size={20} />
          </div>
          <h2 className="album-main-title">
            {t('special.school.album_title', 'Образовательные приключения')}
          </h2>
          <p className="album-subtitle">
            {t('special.school.album_subtitle', 'Уникальные программы для школьных групп: от истории до экологии')}
          </p>
        </div>

        <div className="album-relative-wrapper">
          <button className="album-arrow arrow-left" onClick={() => scroll("left")}>
            <ChevronLeft size={24} />
          </button>

          <div className="album-scroll-row" ref={scrollRef}>
            {schoolToursData.map((tour) => (
              <div className="school-album-card" key={tour.id}>
                <div className="school-card-media">
                  <img src={tour.image} alt="Tour" loading="lazy"/>
                  <span className="age-tag">{tour.age[currentLang] || tour.age['en']}</span>
                </div>
                <div className="school-card-desc">
                  <h4>{tour.title[currentLang] || tour.title['en']}</h4>
                  <p><MapPin size={14} /> {tour.location[currentLang] || tour.location['en']}</p>
                </div>
              </div>
            ))}
          </div>

          <button className="album-arrow arrow-right" onClick={() => scroll("right")}>
            <ChevronRight size={24} />
          </button>
        </div>
      </Container>
    </div>
  );
}


// --- FAQ ДЛЯ КОНСТРУКТОРА ТУРОВ ---
function CustomFAQ({ t }) {
  const questions = [1, 2, 3, 4, 5, 6]; // Добавь сколько нужно
  return (
    <div className="sp-faq-container">
      <h3 className="sp-faq-title">{t("special.faq.custom_title")}</h3>
      <Accordion flush className="sp-minimal-accordion">
        {questions.map((num) => (
          <Accordion.Item eventKey={num.toString()} key={num} className="sp-faq-item">
            <Accordion.Header className="sp-faq-header">
              {t(`special.faq.custom_q${num}`)}
            </Accordion.Header>
            <Accordion.Body className="sp-faq-body">
              {t(`special.faq.custom_a${num}`)}
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  );
}

// --- FAQ ДЛЯ ШКОЛЬНЫХ ТУРОВ ---
function SchoolFAQ({ t }) {
  const questions = [1, 2, 3, 4, 5, 6];
  return (
    <div className="sp-faq-container">
      <h3 className="sp-faq-title">{t("special.faq.school_title")}</h3>
      <Accordion flush className="sp-minimal-accordion">
        {questions.map((num) => (
          <Accordion.Item eventKey={num.toString()} key={num} className="sp-faq-item">
            <Accordion.Header className="sp-faq-header">
              {t(`special.faq.school_q${num}`)}
            </Accordion.Header>
            <Accordion.Body className="sp-faq-body">
              {t(`special.faq.school_a${num}`)}
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  );
}

}


export default SpecialPage;